// Yield Not (§31 A12): the Endurance Monument and the Shield of Defiance.
//
// While a side powers it (Monument ∧ Shield, `poweredCount`):
//
// - **Its buildings** have more max HP: `ARTIFACTS_LATE.obstructionBulwark`
//   (3) on an obstruction, `structureBulwark` (1) on any other structure —
//   `Structure.bulwark`, read by `structureMaxHealth`. Gaining it adds the
//   HP; losing it takes the same off, never below 1. Flat, not per Monument.
//   **Belt parts are not structures here**: they keep `BELT_ROW`'s 1 HP and no
//   health store (a ruling — the design's "other structures" predates S2).
// - **The bearer** wears `ARTIFACTS_LATE.defiance` (10) more armor:
//   `addPlating` when a body comes to bear it powered, `reframePlating`
//   (−10, plate and cap) when it stops — the gear's `granted` names the body.
//
// Primary state both ways (health, plating, `bulwark`, `granted` are hashed),
// so it is settled by one diff in `applyCommand` after `syncGear`, like the
// gear it reads.

import type { GameState } from "../commands";
import { gearOf, gearPowered, poweredCount } from "../industry/gear";
import { cloneIndustry, ENDURANCE_MONUMENT, type Gear, structureAt, structureSquares } from "../industry/state";
import { isObstructionKind, structureHealthOf } from "../industry/structures";
import { addPlating, clonePosition, reframePlating, setStructureHealth } from "../position";
import { ARTIFACTS_LATE } from "../rules";

/** The bulwark a structure of `owner`'s of this kind should carry now. */
function bulwarkFor(on: boolean, obstruction: boolean): number {
  if (!on) return 0;
  return obstruction ? ARTIFACTS_LATE.obstructionBulwark : ARTIFACTS_LATE.structureBulwark;
}

/** The body a Shield of Defiance should be granting its armor to now, or 0. */
const grantee = (industry: GameState["industry"], gear: Gear): number =>
  gear.kind === ENDURANCE_MONUMENT && gear.holder > 0 && gearPowered(industry, gear) ? gear.holder : 0;

/** Settle Yield Not's HP and armor against the board. Returns `next` itself when nothing moves. */
export function settleDefiance(state: GameState, next: GameState): GameState {
  const { industry } = next;
  const powered = new Map<number, boolean>();
  const on = (owner: number): boolean => {
    let answer = powered.get(owner);
    if (answer === undefined) {
      answer = poweredCount(industry, owner, ENDURANCE_MONUMENT) > 0;
      powered.set(owner, answer);
    }
    return answer;
  };

  const walls: [number, number][] = [];
  for (const sq of structureSquares(industry)) {
    const structure = structureAt(industry, sq)!;
    // Nothing can have a bulwark on a board with no gear and none carried.
    if (!industry.gear && !structure.bulwark) continue;
    const want = bulwarkFor(on(structure.owner), isObstructionKind(structure.kind));
    if ((structure.bulwark ?? 0) !== want) walls.push([sq, want]);
  }
  const gear = gearOf(industry);
  // **A Shield pruned with its Monument** (`syncGear` drops gear whose anchor
  // fell) takes its grant with it: every grant the command started with that
  // no surviving gear still carries is revoked.
  const orphaned = new Map<number, number>();
  for (const one of state.industry ? gearOf(state.industry) : []) {
    if (one.granted) orphaned.set(one.granted, (orphaned.get(one.granted) ?? 0) + 1);
  }
  for (const one of gear) {
    const left = one.granted ? orphaned.get(one.granted) : undefined;
    if (left !== undefined) {
      if (left > 1) orphaned.set(one.granted!, left - 1);
      else orphaned.delete(one.granted!);
    }
  }
  const grants = orphaned.size > 0 || gear.some((one) => (one.granted ?? 0) !== grantee(industry, one));
  if (walls.length === 0 && !grants) return next;

  const ind = industry === state.industry || industry.structures === state.industry?.structures ? cloneIndustry(industry) : industry;
  const position = next.position === state.position ? clonePosition(next.position) : next.position;

  for (const [sq, want] of walls) {
    const old = structureAt(ind, sq)!;
    const fresh = { ...old };
    if (want > 0) fresh.bulwark = want;
    else delete fresh.bulwark;
    if (old.health !== undefined) fresh.health = Math.max(1, old.health + want - (old.bulwark ?? 0));
    ind.structures.set(sq, fresh);
    setStructureHealth(position, sq, structureHealthOf(fresh));
  }

  if (grants) {
    const where = new Map<number, number>();
    for (const [sq, id] of position.pieceId) where.set(id, sq);
    for (const [id, times] of orphaned) {
      const from = where.get(id);
      for (let i = 0; from !== undefined && i < times; i++) reframePlating(position, from, -ARTIFACTS_LATE.defiance);
    }
    ind.gear = gear.map((one) => {
      const want = grantee(ind, one);
      const had = one.granted ?? 0;
      if (had === want) return one;
      const from = had > 0 ? where.get(had) : undefined;
      if (from !== undefined) reframePlating(position, from, -ARTIFACTS_LATE.defiance);
      const to = want > 0 ? where.get(want) : undefined;
      if (to !== undefined) addPlating(position, to, ARTIFACTS_LATE.defiance);
      const { granted: _old, ...rest } = one;
      return to !== undefined ? { ...rest, granted: want } : rest;
    });
  }
  return { ...next, industry: ind, position };
}
